
import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, Filter, X } from 'lucide-react';
import { CommentFilter } from '@/hooks/comments/useCardCommentsWithReplies';

interface CommentFiltersProps {
  filter: CommentFilter;
  searchQuery: string;
  isSearching: boolean;
  totalComments: number;
  onFilterChange: (filter: CommentFilter) => void;
  onSearchChange: (query: string) => void;
  onClearFilters: () => void;
}

const CommentFilters: React.FC<CommentFiltersProps> = ({
  filter,
  searchQuery,
  isSearching,
  totalComments,
  onFilterChange,
  onSearchChange,
  onClearFilters
}) => {
  const hasActiveFilters = filter.type !== 'all' || searchQuery.trim() !== '';
  
  const filterOptions = [
    { value: 'all', label: 'All Comments' },
    { value: 'verified', label: 'Verified Users' },
    { value: 'anonymous', label: 'Anonymous' },
    { value: 'reported', label: 'Reported' }
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search comments..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
          />
          {isSearching && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          )}
        </div>
        
        {/* Filter Select */}
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select
            value={filter.type}
            onValueChange={(value: CommentFilter['type']) => 
              onFilterChange({ ...filter, type: value })
            }
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {filterOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClearFilters}
            className="flex items-center gap-2"
          >
            <X className="h-3 w-3" />
            Clear
          </Button>
        )}
      </div>
      
      {/* Active Filters Display */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">
          {totalComments} comment{totalComments !== 1 ? 's' : ''} found
        </span>
        
        {hasActiveFilters && (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">•</span>
            <span className="text-muted-foreground">Filters:</span>
            
            {filter.type !== 'all' && (
              <Badge variant="secondary" className="text-xs">
                {filterOptions.find(opt => opt.value === filter.type)?.label}
              </Badge>
            )}
            
            {searchQuery.trim() && (
              <Badge variant="secondary" className="text-xs">
                Search: "{searchQuery.trim()}"
              </Badge>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentFilters;
